import { KEYS } from 'game/constants/SceneConstants';
import { SceneBase } from '@jollywise/jollygoodgame';
import { EndScreen } from './EndScreen';
import GameSceneBase from './GameSceneBase';
import { GameThermometor } from '../../display/ui/games/GameThermometor';
import { GameTimer } from '../../display/ui/games/GameTimer';
import { OnboardingPointer } from '../../display/ui/OnboardingPointer';

const ZONES = [
  {
    id: 'keep_out_the_cold_window_1',
    x: 670,
    y: 175,
    width: 135,
    height: 200,
    temperature: -5,
    autoOpen: true,
    type: 'window',
  },
  {
    id: 'keep_out_the_cold_window_2',
    x: 830,
    y: 175,
    width: 135,
    height: 200,
    temperature: -5,
    autoOpen: true,
    type: 'window',
  },
  {
    id: 'keep_out_the_cold_window_3',
    x: 1000,
    y: 175,
    width: 135,
    height: 200,
    temperature: -5,
    autoOpen: true,
    type: 'window',
  },
  {
    id: 'keep_out_the_cold_door',
    x: 475,
    y: 375,
    width: 200,
    height: 350,
    temperature: -10,
    autoOpen: true,
    type: 'door',
  },
  {
    id: 'keep_out_the_cold_heater',
    x: 1175,
    y: 475,
    width: 150,
    height: 200,
    temperature: 9,
    autoOpen: true,
    type: 'heater',
  },
];

const STATE_RESET_TIME = 2500;
const GAME_TIME_LIMIT = 30000;

export default class KeepOutTheCold extends GameSceneBase {
  constructor() {
    super({ key: KEYS.KeepOutTheCold, active: false });
  }

  create() {
    super.create();
    this.gameRunning = false;
    this.timelimit = GAME_TIME_LIMIT;
    this.spineBG = this.add.spine(840, 360, 'keep_out_the_cold_background', '', true);

    this.interactives = Object.keys(ZONES).map((k) => this.addInteractiveZone(ZONES[k]));

    this._spines = [this.spineBG, ...this.interactives.map((i) => i.spine)];

    this.thermomemtor = new GameThermometor(this, 1175, 200);
    this.gameClock = new GameTimer(this, 550, 0);
    // this.gameClock.setTimePercent(0);
    this.onboardingPointer = new OnboardingPointer(this, {
      tapPositions: ZONES.map((z) => ({ x: z.x, y: z.y })),
    }).setDepth(1);

    this.setUpGame();
  }

  setUpGame() {
    this.thermomemtor.visible = false;
    this.gameClock.visible = false;
    this.timelimit = GAME_TIME_LIMIT;
    this.timeoutClosedObject = STATE_RESET_TIME;
    this.temperature = 50;

    this.spineBG.once('complete', this.showInstruction, this);
    this.spineBG.on('event', this.handleSpineEvent, this);
    this._spines.forEach((s) => s.play('intro', false));
  }

  handleSpineEvent(t, e) {
    if (e.data?.name === 'sfx') {
      switch (e.stringValue) {
        case 'page_open':
          this.game.soundController.playSFX('general', 'general_page_open');
          break;
        case 'page_close':
          this.game.soundController.playSFX('general', 'general_page_close');
          break;
        case 'page_popup':
          this.game.soundController.playSFX('general', 'general_popup');
          break;
      }
    }
  }

  showInstruction() {
    super.showInstruction(this.game.copy.get('KeepOutTheCold_instruction'), this.startGame, this);
  }

  startGame() {
    this.gameClock.visible = true;
    this.gameClock.enter();
    this.thermomemtor.enter();

    this.spineBG.setAnimation(0, 'idle', true);
    this.interactives.forEach((i) => this.setInteractiveState(i, 'open'));

    this.breezeFX = this.game.soundController.playSFX('keepoutthecold', 'cold_window_opening', {
      loop: true,
    });
    this.thermomemtor.once('complete', () => (this.gameRunning = true), this);
    this.enableInteractiveZones(true);
  }

  addInteractiveZone(config) {
    const zone = this.add.zone(config.x, config.y, config.width, config.height);
    zone.spine = this.add.spine(840, 360, config.id, '', true);
    zone.state = 'closed';
    zone.on('pointerdown', () => this.handleSelectInteractive(zone), this);
    zone.id = config.id;
    zone.temperature = config.temperature;
    zone.autoOpen = config.autoOpen;
    zone.type = config.type;
    return zone;
  }

  enableInteractiveZones(value) {
    this.interactives.forEach((i) => i.setInteractive(value ? { useHandCursor: true } : false));
    if (value) this.onboardingPointer.enable();
    else this.onboardingPointer.disable();
  }

  update(t, delta) {
    if (!this.gameRunning) return;

    this.interactives.forEach((interactive) => this.updateInteractive(interactive, delta));

    this.thermomemtor.setTimePercent(this.temperature / 100);

    this.timeoutClosedObject -= delta;
    if (this.timeoutClosedObject <= 0) {
      const objects = this.interactives.filter((i) => i.state === 'closed' && i.autoOpen);
      if (objects?.length)
        this.setInteractiveState(objects[Math.floor(Math.random() * objects.length)], 'open');
      this.timeoutClosedObject = STATE_RESET_TIME;

      objects.forEach((i) => {
        if (i.state === 'open') {
          if (i.type === 'door') {
            this.game.soundController.playSFX('keepoutthecold', 'cold_door_open');
          }
          if (i.type === 'heater') {
            this.game.soundController.playSFX('keepoutthecold', 'cold_heater_on');
          }

          if ((i.type === 'window' && !this.breezeFX) || (i.type === 'door' && !this.breezeFX)) {
            this.breezeFX.play();
          }
        }
      });

      this.updateAudioFX();
    }

    this.timelimit -= delta;
    this.gameClock.setTimePercent(this.timelimit / GAME_TIME_LIMIT);
    if (this.timelimit < 0) this.handleGameEnd();
  }

  handleSelectInteractive(interactive) {
    this.setInteractiveState(interactive, interactive.state === 'open' ? 'closed' : 'open');

    if (interactive.type === 'door') {
      this.game.soundController.playSFX(
        'keepoutthecold',
        interactive.state === 'closed' ? 'cold_door_close' : 'cold_door_open'
      );
    }

    if (interactive.type === 'heater') {
      this.game.soundController.playSFX(
        'keepoutthecold',
        interactive.state === 'closed' ? 'cold_heater_off' : 'cold_heater_on'
      );
    }
    this.updateAudioFX();
  }

  updateAudioFX() {
    this.openables = this.interactives.filter((i) => i.type === 'window' || i.type === 'door');

    this.interactiveOpen = this.openables.find((i) => i.state === 'open');

    if (!this.interactiveOpen) {
      this.breezeFX.pause();
    } else if (this.interactiveOpen && !this.breezeFX.isPlaying) {
      this.breezeFX.resume();
    }
  }

  setInteractiveState(interactive, state) {
    interactive.spine.play(state, false);
    interactive.state = state;
    interactive.stateDuration = 0;
  }

  updateInteractive(interactive, delta) {
    if (interactive.state !== 'closed') {
      const temp = interactive.temperature * (delta / 1000);
      this.temperature = Math.max(0, Math.min(100, this.temperature + temp));
    }
  }

  handleGameEnd() {
    this.enableInteractiveZones(false);
    this.gameClock.exit();
    this.gameRunning = false;
    const didWin = this.temperature > 20 && this.temperature < 80;
    if (didWin) this.winGame();
    else this.failGame();
  }

  failGame() {
    this.game.soundController.stopMusic('music');
    this.interactives.forEach((i) => i.spine.play('out'));
    this.spineBG.play('lose');
    this.game.soundController.playSFX('general', 'general_fail', {
      delay: 1.2,
    });
    this.game.soundController.playSFX('keepoutthecold', 'cold_teeth', { delay: 2.2 });
    this.spineBG.once('complete', () => {
      this.showEndGameScreen(false, this.temperature > 80 ? 1 : 0);
    });
  }

  winGame() {
    this.game.soundController.stopMusic('music');
    this.interactives.forEach((i) => i.spine.play('out'));
    this.spineBG.play('win');
    this.game.soundController.playSFX('general', 'general_success', {
      delay: 1.2,
    });
    this.game.soundController.playSFX('keepoutthecold', 'cold_cheer', { delay: 2.2 });
    this.spineBG.once('complete', () => {
      this.showEndGameScreen(true, this.temperature > 40 && this.temperature < 60 ? 3 : 2);
    });
  }

  restartGame() {
    this.endScreen?.destroy();
    this.endScreen = null;
    this.setUpGame();
  }

  shutdown() {
    this.game.soundController.stopAllSFX();
    super.shutdown();
    this.game.unloadGame(KEYS.KeepOutTheCold);
  }
}
